import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, CalendarCheck, Settings, Package, Image, MessageSquare, HelpCircle, Star,
  FileText, Download, Check, X, Clock, Plus, Trash2, Ban, Mail, Palette, Users, RefreshCw,
} from "lucide-react";
import { api, brand } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";

const STATUSES = ["Pending", "Awaiting Deposit", "Confirmed", "Completed", "Cancelled", "Declined"];
const statusColor = {
  Pending: "bg-sunshine", "Awaiting Deposit": "bg-purple text-white", Confirmed: "bg-leaf text-white",
  Completed: "bg-brightblue", Cancelled: "bg-overall text-white", Declined: "bg-hotpink text-white",
};

const TABS = [
  { id: "bookings", label: "Bookings", icon: CalendarCheck },
  { id: "availability", label: "Availability", icon: Settings },
  { id: "packages", label: "Packages", icon: Package },
  { id: "gallery", label: "Gallery", icon: Image },
  { id: "testimonials", label: "Testimonials", icon: Star },
  { id: "faqs", label: "FAQs", icon: HelpCircle },
  { id: "content", label: "Site Content", icon: FileText },
  { id: "messages", label: "Messages", icon: MessageSquare },
];

export default function Admin() {
  useSeo({ title: "Admin Dashboard" });
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("bookings");

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-ink text-cream border-b-[3px] border-ink">
        <div className="section flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <img src={brand.icon} alt="" className="h-10 w-10 rounded-xl bg-cream" />
            <div>
              <p className="font-heading text-lg leading-tight">Paint With Tay</p>
              <p className="text-xs text-cream/60">Owner Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-cream/80">{user?.email}</span>
            <Link to="/" className="text-sm hover:text-sunshine">View site</Link>
            <button onClick={logout} className="btn-white !py-2 !px-4 text-sm !text-ink" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Log out</button>
          </div>
        </div>
      </header>

      <div className="section py-6">
        <div className="flex gap-2 overflow-x-auto pb-3 mb-4" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full border-[2.5px] border-ink font-heading font-600 transition ${tab === t.id ? "bg-hotpink text-white" : "bg-white text-ink hover:bg-sunshine"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "bookings" && <Bookings />}
        {tab === "availability" && <Availability />}
        {tab === "packages" && <Packages />}
        {tab === "gallery" && <Gallery />}
        {tab === "testimonials" && <Testimonials />}
        {tab === "faqs" && <Faqs />}
        {tab === "content" && <Content />}
        {tab === "messages" && <Messages />}
      </div>
    </div>
  );
}

function SectionCard({ children, className = "" }) {
  return <div className={`card-pop p-5 sm:p-6 ${className}`}>{children}</div>;
}

/* ----------------------------- Bookings ----------------------------- */
function Bookings() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/bookings", { params: filter === "All" ? {} : { status: filter } })
      .then((r) => setItems(r.data || [])).catch(() => toast.error("Failed to load bookings")).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const update = async (id, payload, msg) => {
    try { await api.patch(`/admin/bookings/${id}`, payload); toast.success(msg || "Updated"); load(); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try { await api.delete(`/admin/bookings/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };
  const exportCsv = () => { window.open(`${api.defaults.baseURL}/admin/bookings/export`, "_blank"); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {["All", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`booking-filter-${s.replace(/\s/g, "-")}`}
              className={`px-3 py-1.5 rounded-full border-2 border-ink text-sm font-600 ${filter === s ? "bg-hotpink text-white" : "bg-white"}`}>{s}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-white !py-2 !px-4 text-sm"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button onClick={exportCsv} className="btn-blue !py-2 !px-4 text-sm" data-testid="export-bookings"><Download className="w-4 h-4" /> Export CSV</button>
        </div>
      </div>

      {loading ? <p className="text-ink/60">Loading…</p> : items.length === 0 ? (
        <SectionCard><p className="text-ink/60 text-center py-6">No bookings yet.</p></SectionCard>
      ) : (
        <div className="grid gap-4" data-testid="bookings-list">
          {items.map((b) => (
            <SectionCard key={b.id} className="!p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`chip ${statusColor[b.status] || "bg-white"}`}>{b.status}</span>
                    <span className="chip bg-white capitalize">{b.booking_type}</span>
                    {b.date && <span className="chip bg-white"><Clock className="w-3 h-3" /> {b.date} {b.start_time && `· ${b.start_time}${b.end_time ? `–${b.end_time}` : ""}`}</span>}
                  </div>
                  <h3 className="font-heading text-xl font-700 mt-2">{b.name} <span className="text-ink/50 text-base font-body">· {b.event_type}</span></h3>
                  <p className="text-sm text-ink/70">{b.email}{b.phone ? ` · ${b.phone}` : ""}{b.organization ? ` · ${b.organization}` : ""}</p>
                  <div className="text-sm text-ink/80 mt-2 grid sm:grid-cols-2 gap-x-6">
                    {b.location && <p><strong>Location:</strong> {b.location}</p>}
                    {b.theme && <p><strong>Theme:</strong> {b.theme}</p>}
                    {b.guests != null && <p><strong>Guests:</strong> {b.guests}</p>}
                    {b.faces_expected != null && <p><strong>Faces:</strong> {b.faces_expected}</p>}
                    {b.indoor_outdoor && <p><strong>Setting:</strong> {b.indoor_outdoor}</p>}
                    {b.budget && <p><strong>Budget:</strong> {b.budget}</p>}
                    {b.estimated_price != null && <p><strong>Est. price:</strong> ${b.estimated_price}</p>}
                    {b.addons?.length > 0 && <p><strong>Add-ons:</strong> {b.addons.join(", ")}</p>}
                  </div>
                  {b.special_instructions && <p className="text-sm text-ink/70 mt-1"><strong>Notes:</strong> {b.special_instructions}</p>}
                  {b.admin_note && <p className="text-sm text-purple mt-1"><strong>Your note:</strong> {b.admin_note}</p>}
                  {b.proposed_time && <p className="text-sm text-purple"><strong>Proposed time:</strong> {b.proposed_time}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t-2 border-ink/10">
                <button onClick={() => update(b.id, { status: "Confirmed" }, "Marked confirmed")} className="btn-pop bg-leaf text-white !py-2 !px-3 text-sm" data-testid={`confirm-${b.id}`}><Check className="w-4 h-4" /> Confirm</button>
                <button onClick={() => update(b.id, { status: "Awaiting Deposit" }, "Deposit requested")} className="btn-pop bg-purple text-white !py-2 !px-3 text-sm">Request deposit</button>
                <button onClick={() => update(b.id, { status: "Completed" }, "Marked completed")} className="btn-pop bg-brightblue text-ink !py-2 !px-3 text-sm">Complete</button>
                <button onClick={() => { const t = window.prompt("Propose a different time (free text, e.g. 'Sat 2pm'):"); if (t) update(b.id, { proposed_time: t }, "Alternate time proposed"); }} className="btn-white !py-2 !px-3 text-sm">Propose time</button>
                <button onClick={() => { const n = window.prompt("Add an internal note:"); if (n != null) update(b.id, { admin_note: n }, "Note saved"); }} className="btn-white !py-2 !px-3 text-sm">Note</button>
                <button onClick={() => update(b.id, { status: "Declined" }, "Declined")} className="btn-pop bg-hotpink text-white !py-2 !px-3 text-sm"><X className="w-4 h-4" /> Decline</button>
                <button onClick={() => update(b.id, { status: "Cancelled" }, "Cancelled")} className="btn-white !py-2 !px-3 text-sm"><Ban className="w-4 h-4" /> Cancel</button>
                <button onClick={() => remove(b.id)} className="btn-white !py-2 !px-3 text-sm !text-hotpink"><Trash2 className="w-4 h-4" /></button>
              </div>
            </SectionCard>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------- Availability ----------------------------- */
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
function Availability() {
  const [s, setS] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [newBlock, setNewBlock] = useState({ date: "", reason: "" });

  const load = useCallback(() => {
    api.get("/admin/settings").then((r) => setS(r.data || {})).catch(() => {});
    api.get("/admin/blocked-dates").then((r) => setBlocked(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!s) return <p className="text-ink/60">Loading…</p>;

  const setNum = (k) => (e) => setS({ ...s, [k]: Number(e.target.value) });
  const setDay = (idx, field, val) => {
    const wh = { ...(s.weekly_hours || {}) };
    wh[idx] = { ...(wh[idx] || { enabled: true, open: "09:00", close: "18:00" }), [field]: val };
    setS({ ...s, weekly_hours: wh });
  };
  const save = async () => { try { await api.put("/admin/settings", s); toast.success("Settings saved"); } catch { toast.error("Save failed"); } };
  const addBlock = async () => {
    if (!newBlock.date) return toast.error("Pick a date");
    try { await api.post("/admin/blocked-dates", newBlock); setNewBlock({ date: "", reason: "" }); load(); toast.success("Date blocked"); } catch { toast.error("Failed"); }
  };
  const delBlock = async (id) => { try { await api.delete(`/admin/blocked-dates/${id}`); load(); } catch {} };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <SectionCard>
        <h3 className="font-heading text-2xl font-700 mb-4">Weekly working hours</h3>
        <div className="space-y-2">
          {DAYS.map((d, i) => {
            const wh = (s.weekly_hours || {})[i] || { enabled: false, open: "09:00", close: "18:00" };
            return (
              <div key={d} className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 w-32">
                  <input type="checkbox" checked={!!wh.enabled} onChange={(e) => setDay(i, "enabled", e.target.checked)} className="w-4 h-4" data-testid={`day-${i}-enabled`} />
                  <span className="font-600">{d}</span>
                </label>
                <input type="time" value={wh.open} onChange={(e) => setDay(i, "open", e.target.value)} className="field !py-1.5 !w-32" disabled={!wh.enabled} />
                <span>–</span>
                <input type="time" value={wh.close} onChange={(e) => setDay(i, "close", e.target.value)} className="field !py-1.5 !w-32" disabled={!wh.enabled} />
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard>
        <h3 className="font-heading text-2xl font-700 mb-4">Pricing & booking rules</h3>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center gap-2 col-span-2"><input type="checkbox" checked={!!s.hourly_enabled} onChange={(e) => setS({ ...s, hourly_enabled: e.target.checked })} className="w-4 h-4" /> Show hourly pricing to customers</label>
          <div><label className="label">Hourly rate ($)</label><input type="number" className="field" value={s.hourly_rate || 0} onChange={setNum("hourly_rate")} data-testid="hourly-rate" /></div>
          <div><label className="label">Min hours</label><input type="number" className="field" value={s.min_hours || 1} onChange={setNum("min_hours")} /></div>
          <div><label className="label">Max hours</label><input type="number" className="field" value={s.max_hours || 6} onChange={setNum("max_hours")} /></div>
          <div><label className="label">Slot interval (min)</label><input type="number" className="field" value={s.slot_interval || 30} onChange={setNum("slot_interval")} /></div>
          <div><label className="label">Buffer before (min)</label><input type="number" className="field" value={s.buffer_before || 0} onChange={setNum("buffer_before")} /></div>
          <div><label className="label">Buffer after (min)</label><input type="number" className="field" value={s.buffer_after || 0} onChange={setNum("buffer_after")} /></div>
          <div><label className="label">Min advance (days)</label><input type="number" className="field" value={s.min_advance_days || 0} onChange={setNum("min_advance_days")} /></div>
          <div><label className="label">Booking window (days)</label><input type="number" className="field" value={s.max_window_days || 365} onChange={setNum("max_window_days")} /></div>
          <div><label className="label">Hold time (min)</label><input type="number" className="field" value={s.hold_minutes || 60} onChange={setNum("hold_minutes")} /></div>
          <div>
            <label className="label">Deposit type</label>
            <select className="field" value={s.deposit_type || "none"} onChange={(e) => setS({ ...s, deposit_type: e.target.value })}>
              <option value="none">No online payment</option><option value="fixed">Fixed deposit</option><option value="percent">Percentage deposit</option><option value="full">Full payment</option>
            </select>
          </div>
          <div><label className="label">Deposit value</label><input type="number" className="field" value={s.deposit_value || 0} onChange={setNum("deposit_value")} /></div>
        </div>
        <div className="mt-4"><label className="label">Travel note</label><textarea rows={2} className="field" value={s.travel_note || ""} onChange={(e) => setS({ ...s, travel_note: e.target.value })} /></div>
        <div className="mt-4"><label className="label">Cancellation policy</label><textarea rows={3} className="field" value={s.cancellation_policy || ""} onChange={(e) => setS({ ...s, cancellation_policy: e.target.value })} /></div>
        <button onClick={save} className="btn-pink mt-5 w-full" data-testid="save-settings">Save settings</button>
      </SectionCard>

      <SectionCard className="lg:col-span-2">
        <h3 className="font-heading text-2xl font-700 mb-4">Blocked & unavailable dates</h3>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div><label className="label">Date</label><input type="date" className="field" value={newBlock.date} onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })} data-testid="block-date" /></div>
          <div className="flex-1 min-w-[12rem]"><label className="label">Reason (vacation, personal, booked elsewhere…)</label><input className="field" value={newBlock.reason} onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })} /></div>
          <button onClick={addBlock} className="btn-blue" data-testid="add-block"><Plus className="w-4 h-4" /> Block date</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {blocked.length === 0 && <p className="text-ink/60">No blocked dates.</p>}
          {blocked.map((b) => (
            <span key={b.id} className="chip bg-overall text-white">{b.date}{b.reason ? ` · ${b.reason}` : ""} <button onClick={() => delBlock(b.id)} className="ml-1"><X className="w-3.5 h-3.5" /></button></span>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

/* ----------------------------- Generic CRUD list ----------------------------- */
function useCrud(coll) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { api.get(`/admin/${coll}`).then((r) => setItems(r.data || [])).catch(() => {}); }, [coll]);
  useEffect(() => { load(); }, [load]);
  const create = async (data) => { await api.post(`/admin/${coll}`, data); load(); };
  const update = async (id, data) => { await api.put(`/admin/${coll}/${id}`, data); load(); };
  const remove = async (id) => { await api.delete(`/admin/${coll}/${id}`); load(); };
  return { items, create, update, remove, load };
}

/* ----------------------------- Packages ----------------------------- */
function Packages() {
  const { items, create, update, remove } = useCrud("packages");
  const blank = { name: "", description: "", duration_hours: 2, base_price: 0, max_faces: 0, buffer_before: 30, buffer_after: 30, active: true, order: 99, addons: [] };
  const [form, setForm] = useState(blank);
  const submit = async () => { if (!form.name) return toast.error("Name required"); await create(form); setForm(blank); toast.success("Package added"); };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <SectionCard>
        <h3 className="font-heading text-2xl font-700 mb-4">Add a package</h3>
        <div className="space-y-3">
          <input className="field" placeholder="Package name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pkg-name" />
          <textarea rows={2} className="field" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Duration (h)</label><input type="number" className="field" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: Number(e.target.value) })} /></div>
            <div><label className="label">Base price ($)</label><input type="number" className="field" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: Number(e.target.value) })} /></div>
            <div><label className="label">Max faces</label><input type="number" className="field" value={form.max_faces} onChange={(e) => setForm({ ...form, max_faces: Number(e.target.value) })} /></div>
            <div><label className="label">Order</label><input type="number" className="field" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></div>
          </div>
          <button onClick={submit} className="btn-pink w-full" data-testid="add-package"><Plus className="w-4 h-4" /> Add package</button>
        </div>
      </SectionCard>
      <div className="space-y-3">
        {items.map((p) => (
          <SectionCard key={p.id} className="!p-4">
            <div className="flex justify-between items-start gap-2">
              <div>
                <h4 className="font-heading text-lg font-700">{p.name} · ${p.base_price}</h4>
                <p className="text-sm text-ink/70">{p.description}</p>
                <p className="text-xs text-ink/60 mt-1">{p.duration_hours}h · {p.max_faces} faces</p>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => update(p.id, { active: !p.active })} className={`chip ${p.active ? "bg-leaf text-white" : "bg-overall text-white"}`}>{p.active ? "Active" : "Hidden"}</button>
                <button onClick={() => remove(p.id)} className="chip !text-hotpink"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </SectionCard>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- Gallery ----------------------------- */
const GAL_CATS = ["Animals", "Fantasy", "Superheroes and Games", "Flowers and Nature", "Birthday Parties", "Markets and Events"];
function Gallery() {
  const { items, create, update, remove } = useCrud("gallery");
  const blank = { url: "", caption: "", alt: "", category: GAL_CATS[0], order: 99 };
  const [form, setForm] = useState(blank);
  const submit = async () => { if (!form.url) return toast.error("Image URL required"); await create(form); setForm(blank); toast.success("Image added"); };

  return (
    <div className="space-y-5">
      <SectionCard>
        <h3 className="font-heading text-2xl font-700 mb-2">Add a gallery photo</h3>
        <p className="text-sm text-ink/60 mb-4">Paste an image URL for now — a direct upload area for Tay’s real portfolio photos can be added next.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input className="field" placeholder="Image URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="gal-url" />
          <select className="field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{GAL_CATS.map((c) => <option key={c}>{c}</option>)}</select>
          <input className="field" placeholder="Caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} />
          <input className="field" placeholder="Alt text (accessibility)" value={form.alt} onChange={(e) => setForm({ ...form, alt: e.target.value })} />
        </div>
        <button onClick={submit} className="btn-pink mt-4" data-testid="add-gallery"><Plus className="w-4 h-4" /> Add photo</button>
      </SectionCard>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} className="card-soft overflow-hidden p-0">
            <img src={g.url} alt={g.alt} className="w-full h-36 object-cover" />
            <div className="p-2">
              <p className="text-xs chip">{g.category}</p>
              <p className="font-heading text-sm mt-1">{g.caption}</p>
              <button onClick={() => remove(g.id)} className="text-hotpink text-sm mt-1 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- Testimonials ----------------------------- */
function Testimonials() {
  const { items, create, update, remove } = useCrud("testimonials");
  const blank = { name: "", role: "", quote: "", rating: 5, approved: true, order: 99 };
  const [form, setForm] = useState(blank);
  const submit = async () => { if (!form.quote) return toast.error("Quote required"); await create(form); setForm(blank); toast.success("Testimonial added"); };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <SectionCard>
        <h3 className="font-heading text-2xl font-700 mb-4">Add a testimonial</h3>
        <div className="space-y-3">
          <input className="field" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tes-name" />
          <input className="field" placeholder="Role / event" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
          <textarea rows={3} className="field" placeholder="Quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} />
          <button onClick={submit} className="btn-pink w-full" data-testid="add-testimonial"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </SectionCard>
      <div className="space-y-3">
        {items.map((t) => (
          <SectionCard key={t.id} className="!p-4">
            <p className="italic text-ink/85">“{t.quote}”</p>
            <p className="font-heading font-600 mt-1">{t.name} <span className="text-ink/50 font-body text-sm">· {t.role}</span></p>
            <div className="flex gap-2 mt-2">
              <button onClick={() => update(t.id, { approved: !t.approved })} className={`chip ${t.approved ? "bg-leaf text-white" : "bg-overall text-white"}`}>{t.approved ? "Shown" : "Hidden"}</button>
              <button onClick={() => remove(t.id)} className="chip !text-hotpink"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          </SectionCard>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- FAQs ----------------------------- */
function Faqs() {
  const { items, create, update, remove } = useCrud("faqs");
  const blank = { question: "", answer: "", order: 99 };
  const [form, setForm] = useState(blank);
  const submit = async () => { if (!form.question) return toast.error("Question required"); await create(form); setForm(blank); toast.success("FAQ added"); };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <SectionCard>
        <h3 className="font-heading text-2xl font-700 mb-4">Add an FAQ</h3>
        <div className="space-y-3">
          <input className="field" placeholder="Question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question" />
          <textarea rows={3} className="field" placeholder="Answer" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} />
          <button onClick={submit} className="btn-pink w-full" data-testid="add-faq"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </SectionCard>
      <div className="space-y-3">
        {items.map((f) => (
          <SectionCard key={f.id} className="!p-4">
            <h4 className="font-heading font-600">{f.question}</h4>
            <p className="text-sm text-ink/75 mt-1">{f.answer}</p>
            <button onClick={() => remove(f.id)} className="chip !text-hotpink mt-2"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
          </SectionCard>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- Content ----------------------------- */
function Content() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/content").then((r) => setC(r.data || {})).catch(() => {}); }, []);
  if (!c) return <p className="text-ink/60">Loading…</p>;
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const save = async () => { try { await api.put("/admin/content", c); toast.success("Content saved"); } catch { toast.error("Save failed"); } };
  return (
    <SectionCard className="max-w-2xl">
      <h3 className="font-heading text-2xl font-700 mb-4">Site content & contact details</h3>
      <div className="space-y-3">
        <div><label className="label">Contact email</label><input className="field" value={c.contact_email || ""} onChange={set("contact_email")} data-testid="content-email" /></div>
        <div><label className="label">Contact phone</label><input className="field" value={c.contact_phone || ""} onChange={set("contact_phone")} data-testid="content-phone" /></div>
        <div><label className="label">Instagram URL</label><input className="field" value={c.instagram_url || ""} onChange={set("instagram_url")} /></div>
        <div><label className="label">Service area</label><input className="field" value={c.service_area || ""} onChange={set("service_area")} /></div>
        <div><label className="label">Hero headline</label><input className="field" value={c.hero_headline || ""} onChange={set("hero_headline")} /></div>
        <div><label className="label">Hero subtext</label><textarea rows={2} className="field" value={c.hero_subtext || ""} onChange={set("hero_subtext")} /></div>
        <button onClick={save} className="btn-pink w-full" data-testid="save-content">Save content</button>
      </div>
    </SectionCard>
  );
}

/* ----------------------------- Messages ----------------------------- */
function Messages() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/messages").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  return (
    <div className="space-y-3">
      {items.length === 0 && <SectionCard><p className="text-ink/60 text-center py-6">No messages yet.</p></SectionCard>}
      {items.map((m) => (
        <SectionCard key={m.id} className="!p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Mail className="w-4 h-4 text-hotpink" />
            <span className="font-heading font-600">{m.name}</span>
            <span className="text-sm text-ink/60">{m.email}{m.phone ? ` · ${m.phone}` : ""}</span>
            {m.event_type && <span className="chip">{m.event_type}</span>}
            {m.event_date && <span className="chip"><Clock className="w-3 h-3" /> {m.event_date}</span>}
          </div>
          <p className="text-ink/80 mt-2">{m.message}</p>
        </SectionCard>
      ))}
    </div>
  );
}
